import styled, { css } from 'styled-components';

import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import { scale130, scale150, spaceS, spaceXS } from '@xing-com/tokens';

import type { ContentSwitcherSize } from '.';

type ContentSwitcherTransientProps = {
  $size?: ContentSwitcherSize;
  $sizeConfined?: ContentSwitcherSize;
  $sizeWide?: ContentSwitcherSize;
};

type ButtonListTransientProps = {
  $noDropdown?: boolean;
  $size?: ContentSwitcherSize;
  $sizeConfined?: ContentSwitcherSize;
  $sizeWide?: ContentSwitcherSize;
};

// *** Sizes ***
export const sizes = {
  // SpaceS is needed to allow that the Focus Ring is visible
  small: css`
    height: calc(${scale130} + ${spaceS});
  `,
  medium: css`
    height: calc(${scale150} + ${spaceS});
  `,
};

// *** Variants ***
export const variants = {
  filled: null,
};

// *** Components ***
export const ContentSwitcher = styled.nav<ContentSwitcherTransientProps>`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};
`;

export const ButtonList = styled.ul<ButtonListTransientProps>`
  display: flex;
  flex-grow: 1;
  list-style-type: none;
  margin: 0;
  padding: 0;

  /* FF */
  scrollbar-width: none;

  /* Hide horizontal scroll bar */
  overflow: hidden;
  overflow-x: scroll;

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
    height: 0;
    width: 0;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $noDropdown }) => {
    return (
      $noDropdown &&
      css`
        && {
          flex-wrap: nowrap;
          min-width: 100%;
        }
      `
    );
  }};
`;

export const ButtonListItem = styled.li`
  /* The item needs extra space or the focus ring is cut off */
  padding-block: ${spaceXS};

  &:first-child {
    padding-inline-start: ${spaceXS};
  }
`;
